import {Controller, useForm, useFormContext} from "react-hook-form";
import {Alert, Button, Dropdown, Menu, message, Select, Space, Spin} from "antd";
import {SessionAvailabilityFormFieldType} from "./types";
import {FormField, FormFieldError} from "../../../../../design-system/form-field";
import {
  useAddDaysToScheduleApi,
  useFetchSessionsUsingSchedule,
  useGetUserSchedule,
} from "../../../../../react-query-hooks/schedule";
import {useOfferingSchedules} from "@/features/availability";
import {LoadingSpinner} from "../../../../icons/icons";
import {DownOutlined, EditOutlined} from "@ant-design/icons";
import {TAvailabilityApiResponse} from "../../../../../react-query-hooks/availability";
import {
  StyledSessionAvailabilityList,
  StyledSessionAvailabilityListDay,
  StyledSessionAvailabilityListDayValue,
  StyledSessionAvailabilityListDrawer,
  StyledSessionAvailabilityListDrawerInfoBlock,
  StyledSessionAvailabilityListDrawerInfoBlockScheduleName,
  StyledSessionAvailabilityListHeader,
  StyledSessionAvailabilityListItem,
} from "./styles";
import {useState} from "react";
import {IAvailabilityFormValues} from "../../../../calls/availability/i-availability-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {AvailabilityFormSchema} from "../../../../calls/availability/availability-validation-schema";
import moment from "moment";
import {AddDaysToScheduleApiPayload} from "../../../../../react-query-hooks/schedule/types";
import {normalizeSessionsListResponse} from "../../../../../react-query-hooks/availability/availability-normalizer";
import {StyledScheduleAvailabilityFormWrapper} from "../../../../../availability/use-cases/schedule/get-schedules/components/schedule-item/styles";
import {DayTimePicker} from "../../../../day-time-picker";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {scrollToTop} from "../../../../../utils/scroll-to-top";

/**
 * Renders the schedule picker. It deliberately does NOT seed
 * `availabilityScheduleId` — this panel lives inside a collapsed accordion section
 * (`offering-form/accordion/accordion-item`), so it may never mount. Defaults are
 * resolved by whoever owns the form's `defaultValues`; see `useOfferingSchedules`.
 */
export function SessionAvailabilityFormFields() {
  const {control} = useFormContext<SessionAvailabilityFormFieldType>();
  const getUserSchedules = useOfferingSchedules();

  if (getUserSchedules.isLoading) {
    return (
      <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
        <Spin indicator={LoadingSpinner} />
      </div>
    );
  }

  if (getUserSchedules.isError) {
    return (
      <Alert
        message={getUserSchedules.errorMessage ?? "We couldn't load your schedules"}
        type="error"
      />
    );
  }

  const userScheduleOptions = getUserSchedules.schedules.map((s) => ({
    label: s.name,
    value: s.id,
  }));

  return (
    <Space direction="vertical" size={16} style={{display: "flex"}}>
      <div>
        <FormField
          id={"session-availability"}
          label={"Select schedule"}
          renderInput={(id) => (
            <Controller
              control={control}
              name="availabilityScheduleId"
              render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
                <Space direction="vertical" size={16} style={{display: "flex"}}>
                  <Select
                    id={id}
                    size="large"
                    status={error?.message ? "error" : undefined}
                    style={{width: "100%"}}
                    onChange={onChange}
                    value={value}
                    ref={ref}
                    onBlur={onBlur}
                    options={userScheduleOptions}
                    placeholder={"Please select"}
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
        />
      </div>
      <SessionAvailabilityListFormField />
    </Space>
  );
}

export function SessionAvailabilityListFormField() {
  const {watch} = useFormContext<SessionAvailabilityFormFieldType>();
  const scheduleId = watch("availabilityScheduleId");
  const getUserSchedule = useGetUserSchedule(scheduleId);
  const fetchSessionsUsingSchedule = useFetchSessionsUsingSchedule(scheduleId);
  const [isEditAvailabilityDrawer, setIsEditAvailabilityDrawer] = useState(false);

  if (getUserSchedule.isLoading) {
    if (scheduleId) {
      return (
        <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
          <Spin indicator={LoadingSpinner} />
        </div>
      );
    }
    return null;
  }
  if (getUserSchedule.isError) {
    return <Alert message={getUserSchedule.error.message} type="error" />;
  }

  if (scheduleId && fetchSessionsUsingSchedule.isLoading) {
    return (
      <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
        <Spin indicator={LoadingSpinner} />
      </div>
    );
  }

  const sessionsUsingSchedule = normalizeSessionsListResponse(
    fetchSessionsUsingSchedule.data ?? {sessions: []}
  ).sessions;

  const menu = (
    <Menu>
      {sessionsUsingSchedule.map((s) => (
        <Menu.Item key={s.id}>
          <p>{s.name}</p>
        </Menu.Item>
      ))}
    </Menu>
  );

  function handleAvailabilities(): TAvailabilityApiResponse {
    if (getUserSchedule.data) {
      return {
        fri: getUserSchedule.data.availabilities.fri.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        mon: getUserSchedule.data.availabilities.mon.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        sat: getUserSchedule.data.availabilities.sat.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        sun: getUserSchedule.data.availabilities.sun.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        thu: getUserSchedule.data.availabilities.thu.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        tue: getUserSchedule.data.availabilities.tue.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        wed: getUserSchedule.data.availabilities.wed.map((s) => ({
          from: s.from,
          to: s.to,
        })),
      };
    } else {
      return {
        fri: [],
        mon: [],
        sat: [],
        sun: [],
        thu: [],
        tue: [],
        wed: [],
      };
    }
  }
  const availabilities = handleAvailabilities();

  const showEditAvailabilityDrawer = () => {
    setIsEditAvailabilityDrawer(true);
  };

  const onEditAvailabilityDrawerClose = () => {
    setIsEditAvailabilityDrawer(false);
  };

  return (
    <StyledSessionAvailabilityList>
      <Space direction="vertical" size={8} style={{display: "flex"}}>
        <StyledSessionAvailabilityListHeader>
          <p>Weekly hours</p>{" "}
          <div>
            <Button
              type="text"
              shape="circle"
              size="small"
              onClick={showEditAvailabilityDrawer}
            >
              <EditOutlined />
            </Button>
          </div>
        </StyledSessionAvailabilityListHeader>
        <div>
          <Space direction="vertical" size={8} style={{display: "flex"}}>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>SUN</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.sun.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.sun.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>MON</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.mon.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.mon.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>TUE</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.tue.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.tue.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>WED</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.wed.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.wed.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>THU</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.thu.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.thu.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>FRI</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.fri.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.fri.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
            <StyledSessionAvailabilityListItem>
              <StyledSessionAvailabilityListDay>SAT</StyledSessionAvailabilityListDay>
              <div>
                {availabilities.sat.length < 1 ? (
                  <StyledSessionAvailabilityListDayValue>
                    unavailable
                  </StyledSessionAvailabilityListDayValue>
                ) : (
                  availabilities.sat.map((a, index) => (
                    <StyledSessionAvailabilityListDayValue key={index}>
                      {a.from} - {a.to}
                    </StyledSessionAvailabilityListDayValue>
                  ))
                )}
              </div>
            </StyledSessionAvailabilityListItem>
          </Space>
        </div>
      </Space>
      <StyledSessionAvailabilityListDrawer
        title="Edit availability"
        placement="right"
        onClose={onEditAvailabilityDrawerClose}
        visible={isEditAvailabilityDrawer}
        // The form below seeds react-hook-form defaultValues from `availabilities`, which
        // RHF reads only on mount. Without this the drawer would keep its first-render
        // state for the life of the step and show stale days on reopen.
        destroyOnClose
        // width={650}
        size="large"
      >
        <Space direction="vertical" size={16} style={{display: "flex"}}>
          <StyledSessionAvailabilityListDrawerInfoBlock>
            <Space direction="vertical" size={8} style={{display: "flex"}}>
              <StyledSessionAvailabilityListDrawerInfoBlockScheduleName>
                {getUserSchedule.data?.schedule?.name ?? "Schedule"}
              </StyledSessionAvailabilityListDrawerInfoBlockScheduleName>
              <p>
                Changes made here will be applied to{" "}
                <Dropdown overlay={menu}>
                  <a onClick={(e) => e.preventDefault()}>
                    <Space>
                      <span>
                        <b>{getUserSchedule.data.schedule.sessionCount} session(s)</b>
                      </span>
                      <DownOutlined style={{fontSize: "1.2rem"}} />
                    </Space>
                  </a>
                </Dropdown>
              </p>
            </Space>
          </StyledSessionAvailabilityListDrawerInfoBlock>
          <SessionScheduleAvailabilityForm
            availabilities={availabilities}
            scheduleId={scheduleId}
            onEditAvailabilityDrawerClose={onEditAvailabilityDrawerClose}
          />
        </Space>
      </StyledSessionAvailabilityListDrawer>
    </StyledSessionAvailabilityList>
  );
}

export const SessionScheduleAvailabilityForm = ({
  availabilities,
  scheduleId,
  onEditAvailabilityDrawerClose,
}: {
  availabilities: TAvailabilityApiResponse;
  scheduleId: string;
  onEditAvailabilityDrawerClose: () => void;
}) => {
  const addDaysToScheduleMutation = useAddDaysToScheduleApi();

  const {
    handleSubmit,
    formState: {errors},
    watch,
    setValue,
    control,
  } = useForm<IAvailabilityFormValues>({
    resolver: yupResolver(AvailabilityFormSchema),
    mode: "onChange",
    defaultValues: {
      sun: {
        isAvailable: availabilities.sun.length > 0,
        timesAvailable:
          availabilities.sun.length > 0
            ? availabilities.sun
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      mon: {
        isAvailable: availabilities.mon.length > 0,
        timesAvailable:
          availabilities.mon.length > 0
            ? availabilities.mon
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      tue: {
        isAvailable: availabilities.tue.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.tue.length > 0
            ? availabilities.tue
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      wed: {
        isAvailable: availabilities.wed.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.wed.length > 0
            ? availabilities.wed
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      thu: {
        isAvailable: availabilities.thu.length > 0,
        timesAvailable:
          availabilities.thu.length > 0
            ? availabilities.thu
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      fri: {
        isAvailable: availabilities.fri.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.fri.length > 0
            ? availabilities.fri
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      sat: {
        isAvailable: availabilities.sat.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.sat.length > 0
            ? availabilities.sat
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
    },
  });

  function handleCancel() {
    scrollToTop(".ant-drawer-body");
    onEditAvailabilityDrawerClose();
  }

  const sortTimeSlot = (
    arr: {from: string; to: string}[]
  ): {from: string; to: string}[] => {
    const mapResult = arr.map((entry) => {
      const fromEntry = moment(`11-18-2013 ${entry.from}`, "MM-DD-YYYY hh:mm A", true);
      return {
        from: fromEntry,
        to: entry.to,
      };
    });
    const sortResult = mapResult.sort((a, b) => a.from.diff(b.from));
    const finalResult = sortResult.map((res) => ({
      from: res.from.format("hh:mm A"),
      to: res.to,
    }));
    // console.log({finalResult});
    return finalResult;
  };

  const onSubmit = async (values: IAvailabilityFormValues) => {
    const timeEntries = Object.entries(values);
    const payload: AddDaysToScheduleApiPayload = {
      scheduleId,
      fri: [{from: "09:00 AM", to: "05:00 PM"}],
      mon: [{from: "09:00 AM", to: "05:00 PM"}],
      sat: [{from: "09:00 AM", to: "05:00 PM"}],
      sun: [{from: "09:00 AM", to: "05:00 PM"}],
      thu: [{from: "09:00 AM", to: "05:00 PM"}],
      tue: [{from: "09:00 AM", to: "05:00 PM"}],
      wed: [{from: "09:00 AM", to: "05:00 PM"}],
    };
    timeEntries.forEach((entry) => {
      if (entry[0] === "mon") {
        if (entry[1].isAvailable) {
          payload.mon = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.mon = [];
        }
      }
      if (entry[0] === "tue") {
        if (entry[1].isAvailable) {
          payload.tue = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.tue = [];
        }
      }
      if (entry[0] === "wed") {
        if (entry[1].isAvailable) {
          payload.wed = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.wed = [];
        }
      }
      if (entry[0] === "thu") {
        if (entry[1].isAvailable) {
          payload.thu = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.thu = [];
        }
      }
      if (entry[0] === "fri") {
        if (entry[1].isAvailable) {
          payload.fri = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.fri = [];
        }
      }
      if (entry[0] === "sat") {
        if (entry[1].isAvailable) {
          payload.sat = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.sat = [];
        }
      }
      if (entry[0] === "sun") {
        if (entry[1].isAvailable) {
          payload.sun = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.sun = [];
        }
      }
    });
    addDaysToScheduleMutation.mutate(payload, {
      onSuccess: () => {
        message.success("Your availability preferences has been updated successfully");
        handleCancel();

        // console.log({successAddDaysToSchedule: "true"});
      },
    });
  };

  return (
    <div>
      <Space size={24} direction="vertical" style={{display: "Flex"}}>
        <StyledScheduleAvailabilityFormWrapper>
          <p className="availability-form-heading">
            Set your availability and weekly work hours
          </p>
          {/* <form onSubmit={handleSubmit(onSubmit)}> */}
          <div>
            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="SUN"
              checkboxId="SUN"
              checkboxName="sun.isAvailable"
              // checkboxError={errors.sunday?.isAvailable?.message}
              control={control}
              timePickerId="sun"
              timePickerName="sun.timesAvailable"
              timePickerError={errors.sun}
            />

            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="MON"
              checkboxId="mon"
              checkboxName="mon.isAvailable"
              // checkboxError={errors.monday?.isAvailable?.message}
              control={control}
              timePickerId="mon"
              timePickerName="mon.timesAvailable"
              timePickerError={errors.mon}
            />
            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="TUE"
              checkboxId="tue"
              checkboxName="tue.isAvailable"
              // checkboxError={errors.monday?.isAvailable?.message}
              control={control}
              timePickerId="tue"
              timePickerName="tue.timesAvailable"
              timePickerError={errors.tue}
            />
            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="WED"
              checkboxId="wed"
              checkboxName="wed.isAvailable"
              // checkboxError={errors.monday?.isAvailable?.message}
              control={control}
              timePickerId="wed"
              timePickerName="wed.timesAvailable"
              timePickerError={errors.wed}
            />
            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="THU"
              checkboxId="thu"
              checkboxName="thu.isAvailable"
              // checkboxError={errors.monday?.isAvailable?.message}
              control={control}
              timePickerId="thu"
              timePickerName="thu.timesAvailable"
              timePickerError={errors.thu}
            />
            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="FRI"
              checkboxId="fri"
              checkboxName="fri.isAvailable"
              // checkboxError={errors.monday?.isAvailable?.message}
              control={control}
              timePickerId="fri"
              timePickerName="fri.timesAvailable"
              timePickerError={errors.fri}
            />
            <DayTimePicker
              watch={watch}
              setValue={setValue}
              checkboxLabel="SAT"
              checkboxId="SAT"
              checkboxName="sat.isAvailable"
              // checkboxError={errors.monday?.isAvailable?.message}
              control={control}
              timePickerId="sat"
              timePickerName="sat.timesAvailable"
              timePickerError={errors.sat}
            />

            <StyledServiceButtonWrapper style={{marginTop: "2.4rem"}}>
              <div className="secondary-btn">
                <Button onClick={handleCancel} block size="small">
                  Cancel
                </Button>
              </div>
              <div>
                <Button
                  loading={addDaysToScheduleMutation.isLoading}
                  block
                  type="primary"
                  htmlType="button"
                  size="small"
                  onClick={handleSubmit(onSubmit)}
                >
                  Update changes
                </Button>
              </div>
            </StyledServiceButtonWrapper>
          </div>
          {/* </form> */}
        </StyledScheduleAvailabilityFormWrapper>
      </Space>
    </div>
  );
};
